/**
 * Vue安全指令
 * 用于输入验证和XSS防护
 */

import { detectXSS, escapeHtml } from '../utils/security';

/**
 * v-safe-input 指令
 * 自动检测和过滤危险输入
 * 
 * 使用方法：
 * <el-input v-model="username" v-safe-input></el-input>
 */
export const safeInput = {
  bind(el, binding, vnode) {
    const input = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' 
      ? el 
      : el.querySelector('input') || el.querySelector('textarea');
    
    if (!input) return;
    
    input.addEventListener('input', function(e) {
      const value = e.target.value;
      
      // 检测XSS攻击
      if (detectXSS(value)) {
        // 清空输入
        e.target.value = '';
        
        // 显示警告
        if (window.$notify) {
          window.$notify.error({
            title: '安全警告',
            message: '检测到危险字符，输入已被清空',
            duration: 3000
          });
        }
        
        // 触发Vue的input事件
        const event = new Event('input', { bubbles: true });
        e.target.dispatchEvent(event);
      }
    });
  }
};

/**
 * v-escape-html 指令
 * 自动转义HTML特殊字符
 * 
 * 使用方法：
 * <el-input v-model="description" v-escape-html></el-input>
 */
export const escapeHtmlDirective = {
  bind(el, binding, vnode) {
    const input = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' 
      ? el 
      : el.querySelector('input') || el.querySelector('textarea');
    
    if (!input) return;
    
    input.addEventListener('blur', function(e) {
      const value = e.target.value;
      if (value) {
        e.target.value = escapeHtml(value);
        
        // 触发Vue的input事件
        const event = new Event('input', { bubbles: true });
        e.target.dispatchEvent(event);
      }
    });
  }
};

/**
 * v-password-strength 指令
 * 显示密码强度提示
 * 
 * 使用方法：
 * <el-input v-model="password" type="password" v-password-strength></el-input>
 */
export const passwordStrength = {
  bind(el, binding, vnode) {
    const input = el.querySelector('input');
    if (!input) return;
    
    // 创建强度提示元素
    const strengthBar = document.createElement('div');
    strengthBar.style.cssText = `
      height: 4px;
      margin-top: 4px;
      border-radius: 2px;
      transition: all 0.3s;
      background: #eee;
    `;
    el.appendChild(strengthBar);
    
    const strengthText = document.createElement('span');
    strengthText.style.cssText = `
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      display: block;
    `;
    el.appendChild(strengthText);
    
    input.addEventListener('input', function(e) {
      const value = e.target.value;
      
      if (!value) {
        strengthBar.style.width = '0%';
        strengthText.textContent = '';
        return;
      }
      
      // 计算密码强度
      let strength = 0;
      if (value.length >= 8) strength++;
      if (/[a-z]/.test(value)) strength++;
      if (/[A-Z]/.test(value)) strength++;
      if (/[0-9]/.test(value)) strength++;
      if (/[^a-zA-Z0-9]/.test(value)) strength++;
      
      // 更新显示
      if (strength <= 2) {
        strengthBar.style.width = '33%';
        strengthBar.style.background = '#F56C6C';
        strengthText.textContent = '弱';
        strengthText.style.color = '#F56C6C';
      } else if (strength <= 3) {
        strengthBar.style.width = '66%';
        strengthBar.style.background = '#E6A23C';
        strengthText.textContent = '中';
        strengthText.style.color = '#E6A23C';
      } else {
        strengthBar.style.width = '100%';
        strengthBar.style.background = '#67C23A';
        strengthText.textContent = '强';
        strengthText.style.color = '#67C23A';
      }
    });
  }
};

/**
 * v-limit-length 指令
 * 限制输入长度并显示剩余字符
 * 
 * 使用方法：
 * <el-input v-model="content" v-limit-length="100"></el-input>
 */
export const limitLength = {
  bind(el, binding, vnode) {
    const maxLength = binding.value || 100;
    const input = el.querySelector('input') || el.querySelector('textarea');
    if (!input) return;
    
    input.setAttribute('maxlength', maxLength);
    
    const counter = document.createElement('span');
    counter.style.cssText = `
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      display: block;
      text-align: right;
    `;
    el.appendChild(counter);
    
    const updateCounter = () => {
      const length = input.value.length;
      counter.textContent = `${length}/${maxLength}`;
      
      if (length > maxLength * 0.9) {
        counter.style.color = '#F56C6C';
      } else {
        counter.style.color = '#999';
      }
    };
    
    input.addEventListener('input', updateCounter);
    updateCounter();
  }
};

// 全局注册指令的函数
export function registerSecurityDirectives(Vue) {
  Vue.directive('safe-input', safeInput);
  Vue.directive('escape-html', escapeHtmlDirective);
  Vue.directive('password-strength', passwordStrength);
  Vue.directive('limit-length', limitLength);
}




// WEBPACK FOOTER //
// ./src/directives/security.js